import { Grid, IconButton, Typography } from '@mui/material';

export const formulatOperator = {
  sum: '+',
  subtract: '-',
  multiply: 'x',
  divide: '/',
};
export const validateFormula = (items: any, checkListItems: any): boolean => {
  if (!items || items.length < 2) return false;

  // Check each item in the array
  let valid = true;
  items.forEach((item) => {
    const isCustomNumber = item?.type === 'customNumber';
    const checkListItem = isCustomNumber ? item : checkListItems?.[item?.id];
    if (!checkListItem) {
      valid = false;
      return;
    }
    if (checkListItem.type === 'customNumber') {
      if (!checkListItem.value) {
        valid = false;
        return;
      }
    }
    if (
      checkListItem.type !== 'temperature' &&
      checkListItem.type !== 'number' &&
      checkListItem.type !== 'cost' &&
      checkListItem.type !== 'calculation' &&
      checkListItem.type !== 'customNumber'
    ) {
      valid = false;
      return;
    }
  });

  return valid;
};

export const getFormulaView = (formula, checkListItems: any) => {
  const { operator, items } = formula;
  // Check if the operator is "Average"
  const isAverageOperator = operator?.toLowerCase() === 'average';

  if (items?.length >= 2) {
    // Create array of valid item descriptions with operators
    const validItems = items
      .map((item) => {
        const checkListItem =
          item?.type === 'customNumber' ? item : checkListItems[item?.id];
        if (!checkListItem) return null;
        return {
          description: checkListItem.description || checkListItem?.value,
          operator: item.operator || operator,
        };
      })
      .filter((item) => item !== null);

    if (validItems.length === 0) {
      return (
        <Typography fontSize={'13px'} fontWeight={600} color={'#D32F2F'}>
          Invalid Calculation – Missing Step.
        </Typography>
      );
    }

    // Check if we're using individual operators or a global one
    const hasIndividualOperators = items.some((item) => item.operator);

    if (hasIndividualOperators) {
      // Format with individual operators
      const formulaItems = validItems.map((item, index) => {
        if (index === 0) return item.description;

        const prevItem = validItems[index - 1];
        const opSymbol = prevItem?.operator
          ? formulatOperator[prevItem.operator.toLowerCase()] ||
            prevItem.operator
          : formulatOperator[operator.toLowerCase()] || operator;

        return ` ${opSymbol} ${item.description}`;
      });

      return (
        <Typography
          fontSize={'13px'}
          fontWeight={600}
          sx={{ '& span': { fontWeight: 700 } }}
        >
          <span>Formula: </span>
          {formulaItems.join('')}
        </Typography>
      );
    } else if (isAverageOperator) {
      // For average, show all items averaged
      const descriptions = validItems.map((item) => item.description);
      return (
        <Typography
          fontSize={'13px'}
          fontWeight={600}
          sx={{ '& span': { fontWeight: 700 } }}
        >
          <span>Average: </span>({descriptions.join(' + ')}) /{' '}
          {validItems.length}
        </Typography>
      );
    } else {
      // For other global operations, use the normal format
      const operatorSymbol =
        formulatOperator[operator.toLowerCase()] || operator;
      const descriptions = validItems.map((item) => item.description);
      return (
        <Typography
          fontSize={'13px'}
          fontWeight={600}
          sx={{ '& span': { fontWeight: 700 } }}
        >
          <span>{operator}: </span>
          {descriptions.join(` ${operatorSymbol} `)}
        </Typography>
      );
    }
  }

  return (
    <Typography fontSize={'13px'} fontWeight={600} color={'#D32F2F'}>
      Invalid Calculation – Missing Step.
    </Typography>
  );
};

export const getFormulaText = (formula, checkListItems: any) => {
  const { operator = 'sum', items = [] } = (formula && formula.options) || {};

  // Check if the operator is "Average"
  const isAverageOperator = operator?.toLowerCase() === 'average';

  if (items?.length >= 2) {
    // Create array of valid item descriptions with operators
    const validItems = items
      .map((item) => {
        // Handle both old format (string id) and new format (object with id and type)
        const checkListItem = checkListItems[item.id] || item;
        if (!checkListItem) return null;
        return {
          description: checkListItem.description,
          operator: item.operator || operator,
        };
      })
      .filter((item) => item !== null);

    if (validItems.length === 0) {
      return 'Invalid Calculation – Missing Step.';
    }

    // Check if we're using individual operators or a global one
    const hasIndividualOperators = items.some((item) => item.operator);

    if (hasIndividualOperators) {
      // Format with individual operators
      let formulaText = 'Formula: ';

      validItems.forEach((item, index) => {
        if (index === 0) {
          formulaText += item.description;
        } else {
          const prevItem = validItems[index - 1];
          const opSymbol = prevItem?.operator
            ? formulatOperator[prevItem.operator.toLowerCase()] ||
              prevItem.operator
            : formulatOperator[operator.toLowerCase()] || operator;

          formulaText += ` ${opSymbol} ${item.description}`;
        }
      });

      return formulaText;
    } else if (isAverageOperator) {
      // For average, show all items averaged
      const descriptions = validItems.map((item) => item.description);
      return `Average: (${descriptions.join(' + ')}) / ${validItems.length}`;
    } else {
      // For other operations, use the normal format
      const operatorSymbol =
        formulatOperator[operator.toLowerCase()] || operator;
      const descriptions = validItems.map((item) => item.description);
      return `${operator}: ${descriptions.join(` ${operatorSymbol} `)}`;
    }
  }
  return 'Invalid Calculation – Missing Step.';
};

// NOTE: this hash contains two objects with respect to calculation and items
// calculationHash contains the hash of the calculation and there items
// itemsHash contains the hash of the items and respective calculations
export const generateFormulaHashes = (
  templates,
  // selector = 'ChecklistItemId',
  // parentOrder = null,
  result = { calculationsHash: {}, itemsHash: {} },
) => {
  if (templates && templates.length > 0) {
    templates.forEach((template) => {
      const { ChecklistItemId, type, options } = template;

      if (
        type === 'calculation' &&
        options?.items?.length > 1 &&
        options?.operator
      ) {
        // Add calculation to the hash
        if (!result.calculationsHash[ChecklistItemId]) {
          result.calculationsHash[ChecklistItemId] = [];
        }
        options.items.forEach((item) => {
          // Add item to the calculationsHash
          if (
            !result.calculationsHash[ChecklistItemId].includes(
              item.ChecklistItemId,
            )
          ) {
            result.calculationsHash[ChecklistItemId].push(item.id);
          }

          // Initialize the item in itemsHash if it doesn't exist
          if (!result.itemsHash[item.id]) {
            result.itemsHash[item.id] = [];
          }

          // Add the calculation to the itemsHash
          if (!result.itemsHash[item.id].includes(ChecklistItemId)) {
            result.itemsHash[item.id].push(ChecklistItemId);
          }
        });
      }
    });
  }

  return result;
};

export const runCalculations = (
  thisItem,
  updatedChecklistLogs,
  checkListLogHashes,
  calculationHashes,
  onResponseToItem,
) => {
  const itemCalculationsArray =
    calculationHashes?.itemsHash?.[thisItem.ChecklistItemId];
  if (!itemCalculationsArray) return;

  // Create a map of ChecklistItemId to answers for faster lookups
  const itemAnswersMap = new Map();
  updatedChecklistLogs.TaskChecklistItems.forEach((item) => {
    if (item.answers?.value && !item.isHidden) {
      itemAnswersMap.set(item.ChecklistItemId, item.answers.value);
    }
  });

  try {
    for (const calculationItem of itemCalculationsArray) {
      const itemData = calculationHashes?.calculationsHash?.[calculationItem];
      const calculationItemObject = checkListLogHashes[calculationItem];

      const isFormulaValid = validateFormula(
        calculationItemObject?.options?.items,
        checkListLogHashes,
      );
      if (!itemData || !calculationItemObject || !isFormulaValid) continue;

      const defaultOperator = calculationItemObject?.options?.operator || 'Sum';
      const formulaItems = calculationItemObject?.options?.items || [];

      // Check if the operator is "Average"
      const isAverageOperator = defaultOperator?.toLowerCase() === 'average';

      // Get valid number values and operators from related items
      interface ItemValueWithOperator {
        value: number;
        operator: string | null;
      }

      const itemValues: ItemValueWithOperator[] = [];

      // Get values for all items in the formula
      for (let i = 0; i < formulaItems.length; i++) {
        const item = formulaItems[i];
        const itemId = item.id;
        const itemType = item.type;
        let numValue: number | null = null;
        const operator: string | null =
          item.operator ||
          (i < formulaItems.length - 1 ? defaultOperator : null);

        if (itemType === 'customNumber') {
          const rawValue = item?.value;
          numValue = rawValue ? parseFloat(rawValue) : null;
        } else {
          const itemAnswer = itemAnswersMap.get(itemId);
          numValue = itemAnswer ? parseFloat(itemAnswer) : null;
        }

        if (numValue !== null && !isNaN(numValue)) {
          itemValues.push({
            value: numValue,
            operator: operator,
          });
        }
      }

      if (itemValues.length === 0) continue;

      // Handle calculation based on the formula type
      if (itemValues.length === 1) {
        // If there's only one value, just return it
        onResponseToItem(calculationItemObject, itemValues[0].value.toString());
        continue;
      }

      // If operator is Average, calculate the average of all values
      if (isAverageOperator) {
        const sum = itemValues.reduce((acc, item) => acc + item.value, 0);
        const average = sum / itemValues.length;

        // Round to 2 decimal places to avoid floating point issues
        const roundedResult = Math.round(average * 100) / 100;
        onResponseToItem(calculationItemObject, roundedResult.toString());
        continue;
      }

      // For Calculation type, use the optimized DMAS rule
      const values: number[] = [];
      const operators: string[] = [];

      // Collect all values and operators
      values.push(itemValues[0].value);
      for (let i = 1; i < itemValues.length; i++) {
        const op = (
          itemValues[i - 1].operator || defaultOperator
        ).toLowerCase();
        operators.push(op);
        values.push(itemValues[i].value);
      }

      // Simple calculation function that respects order of operations
      const calculate = (vals: number[], ops: string[]): number => {
        // First pass: Process division and multiplication from left to right
        for (let i = 0; i < ops.length; i++) {
          if (ops[i] === 'divide' || ops[i] === 'multiply') {
            if (ops[i] === 'divide') {
              vals[i] = vals[i] / vals[i + 1];
            } else {
              vals[i] = vals[i] * vals[i + 1];
            }

            // Remove the processed value and operator
            vals.splice(i + 1, 1);
            ops.splice(i, 1);

            // Adjust index since we removed an item
            i--;
          }
        }

        // Second pass: Process addition and subtraction from left to right
        let result = vals[0];
        for (let i = 0; i < ops.length; i++) {
          if (ops[i] === 'sum') {
            result += vals[i + 1];
          } else if (ops[i] === 'subtract') {
            result -= vals[i + 1];
          }
        }

        return result;
      };

      // Calculate the result
      const result = calculate(values, operators);

      // Round to 2 decimal places to avoid floating point issues
      const roundedResult = Math.round(result * 100) / 100;
      onResponseToItem(calculationItemObject, roundedResult.toString());
    }
  } catch (error) {
    console.error('Error in runCalculations:', error);
  }
};
